// src/components/rates/Ratechart.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./ratechart.css";
import { useRateRepo } from "../../repos/AppRepoProvider";
import AddEditRatechard from "./AddEditRatechard";
import {
  saveRatechart as saveRatechartApi,
  getRatechart as getRatechartApi,
} from "../../services/ratechart";

const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};
const s = (v) => (v == null ? "" : String(v).trim());

function normMilk(m) {
  const x = s(m).toLowerCase();
  return x === "buf" || x === "buffalo" || x === "b" ? "buf" : "cow";
}

function step01Series(start, count) {
  const out = [];
  const st = n(start);
  const c = Math.max(0, Number(count) || 0);
  for (let i = 0; i < c; i++) out.push((st + i * 0.1).toFixed(1));
  return out;
}

function pointsAxisFromChart(chart) {
  const pts = chart?.points || null;
  const prev = chart?.preview || null;

  const fatSteps = Array.isArray(pts?.fat_steps) ? pts.fat_steps : [];
  const snfSteps = Array.isArray(pts?.snf_steps) ? pts.snf_steps : [];

  const fatStart =
    fatSteps.length > 0 ? n(fatSteps[0].step) : n(prev?.fatStart) || 0;
  const snfStart =
    snfSteps.length > 0 ? n(snfSteps[0].step) : n(prev?.snfStart) || 0;

  const fatCount = Number(prev?.fatCount) || 0;
  const snfCount = Number(prev?.snfCount) || 0;

  const fats = fatCount ? step01Series(fatStart, fatCount) : [];
  const snfs = snfCount ? step01Series(snfStart, snfCount) : [];

  return { fats, snfs };
}

function extractRowsFromData(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  const inner = data.data ?? data.rows ?? data.list ?? data.rate ?? data.table ?? null;
  if (Array.isArray(inner)) return inner;

  if (typeof inner === "string") {
    try {
      const j = JSON.parse(inner);
      if (Array.isArray(j)) return j;
      if (j && Array.isArray(j.rows)) return j.rows;
      if (j && Array.isArray(j.data)) return j.data;
      if (j && Array.isArray(j.points)) return j.points;
    } catch {}
  }
  return [];
}

/**
 * ✅ MUST accept props coming from Dashboard:
 *    <RateChart mainuser={mainuser} selectedKendra={selected} />
 */
export default function RateChart({ mainuser, selectedKendra }) {
  const rateRepo = useRateRepo();

  const [milk, setMilk] = useState("cow");
  const [ratechartno, setRatechartno] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [chartData, setChartData] = useState(null);
  const [sel, setSel] = useState({ fat: null, snf: null });

  // ✅ edit page state (NOT dialog)
  const [editOpen, setEditOpen] = useState(false);

  const mainuserSafe = useMemo(() => s(mainuser), [mainuser]);

  const chartTitle = useMemo(() => {
    return milk === "cow" ? "गाय Rate Chart" : "म्हैस Rate Chart";
  }, [milk]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const t = normMilk(milk);
      const no = Number(ratechartno) === 2 ? 2 : 1;

      setLoading(true);
      setErr("");

      try {
        let data = rateRepo.getRateChart?.(t, no) || null;

        if (!data && rateRepo.refreshRateChart) {
          data = await rateRepo.refreshRateChart(t, no);
        }

        if (cancelled) return;

        setChartData(data || null);

        const isPoints =
          data &&
          String(data.mode || "").toUpperCase() === "POINTS" &&
          data.points;

        if (isPoints) {
          const { fats, snfs } = pointsAxisFromChart(data);
          if (fats.length && snfs.length) setSel({ fat: fats[0], snf: snfs[0] });
          else setSel({ fat: null, snf: null });
        } else {
          setSel({ fat: null, snf: null });
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || "Network Error");
        setChartData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [milk, ratechartno, rateRepo]);

  const isPoints = useMemo(() => {
    return (
      chartData &&
      String(chartData.mode || "").toUpperCase() === "POINTS" &&
      chartData.points
    );
  }, [chartData]);

  const pointsGrid = useMemo(() => {
    if (!isPoints) return { fats: [], snfs: [] };
    return pointsAxisFromChart(chartData);
  }, [isPoints, chartData]);

  const rows = useMemo(() => extractRowsFromData(chartData), [chartData]);

  const isGrid = useMemo(() => {
    if (isPoints) return true;
    return rows.some(
      (x) =>
        x?.snf != null ||
        x?.snf_value != null ||
        x?.snfVal != null ||
        x?.s != null
    );
  }, [rows, isPoints]);

  const cowList = useMemo(() => {
    if (isPoints) return [];
    const out = [];
    for (const r of rows) {
      const fat = n(r.fat ?? r.fat_value ?? r.fatVal ?? r.f);
      const rate = Number(r.rate ?? r.r ?? r.price ?? r.value);
      if (!Number.isFinite(rate)) continue;
      out.push({ fat, rate });
    }
    out.sort((a, b) => a.fat - b.fat);
    return out;
  }, [rows, isPoints]);

  const grid = useMemo(() => {
    if (isPoints) return { fats: [], snfs: [], map: new Map() };

    const map = new Map();
    const fatsSet = new Set();
    const snfsSet = new Set();

    for (const r of rows) {
      const fat = n(r.fat ?? r.fat_value ?? r.fatVal ?? r.f);
      const snf = n(r.snf ?? r.snf_value ?? r.snfVal ?? r.s);
      const rate = Number(r.rate ?? r.r ?? r.price ?? r.value);
      if (!Number.isFinite(rate)) continue;

      const f1 = fat.toFixed(1);
      const s1 = snf.toFixed(1);
      fatsSet.add(f1);
      snfsSet.add(s1);
      map.set(`${f1}|${s1}`, rate);
    }

    const fats = Array.from(fatsSet).sort((a, b) => n(a) - n(b));
    const snfs = Array.from(snfsSet).sort((a, b) => n(a) - n(b));
    return { fats, snfs, map };
  }, [rows, isPoints]);

  const onChangeMilk = (t) => {
    setMilk(normMilk(t));
    setRatechartno(1);
  };

  /**
   * ✅ SAVE from edit page
   * AddEditRatechard will call onSave(metaAndPayload)
   * We build the FINAL envelope here using mainuser from Dashboard props.
   */
  async function saveEditedChart(metaAndPayload) {
    const mu = mainuserSafe;
    if (!mu) {
      setErr("mainuser missing (Dashboard prop not passed)");
      return;
    }

    const t = normMilk(milk);
    const no = Number(ratechartno) === 2 ? 2 : 1;

    setLoading(true);
    setErr("");

    try {
      const body = {
        mainuser: mu,                 // ✅ ONLY from Dashboard
        type: t,                      // ✅ normalized
        ratechartno: no,              // ✅ 1/2
        category: s(metaAndPayload?.category || "COLLECTION"),
        name: s(metaAndPayload?.name || ""),
        charttype: s(metaAndPayload?.charttype || "FAT_SNF"),
        payload: metaAndPayload?.payload || null, // ✅ real chart object
      };

      // quick validation before request
      if (!body.name) throw new Error("Name is required");
      if (!body.payload || typeof body.payload !== "object") throw new Error("Missing payload");

      // ensure serializable
      JSON.stringify(body);

      console.log("RateChart.saveEditedChart() ->", body);

      const resp = await saveRatechartApi(body);
      console.log("save_ratechart.php response ->", resp);

      // refresh after save
      let fresh = null;
      if (rateRepo.refreshRateChart) {
        fresh = await rateRepo.refreshRateChart(t, no);
      } else {
        fresh = await getRatechartApi({ mainuser: mu, type: t, ratechartno: no });
        fresh = fresh?.data ?? fresh;
      }

      if (fresh) setChartData(fresh);
      setEditOpen(false);
    } catch (e) {
      console.error("saveEditedChart failed ->", e);
      setErr(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  }

  // ✅ If editing, show editor page in SAME place
  if (editOpen) {
    return (
      <AddEditRatechard
        open={true}
        milk={milk}
        ratechartno={ratechartno}
        chartData={chartData}
        onClose={() => setEditOpen(false)}
        onSave={saveEditedChart}
        mainuser={mainuserSafe} // optional debug/display
        selectedKendra={selectedKendra}
      />
    );
  }

  return (
    <div className="rcPage">
      <div className="rcHead">
        <div className="rcHeadLeft">
          <span className="rcHeadIcon">▮▮</span>
          <span>Rate Chart</span>
        </div>
        <div className="rcHeadRight">Rate Chart Management</div>
      </div>

      <div className="rcControls">
        <div className="rcControlsLeft">
          <div className="rcMilkLabel">Milk:</div>

          <button
            type="button"
            className={`rcMilkBtn ${milk === "cow" ? "active" : ""}`}
            onClick={() => onChangeMilk("cow")}
          >
            <span className="rcMilkIcon">🐄</span>
            Cow
          </button>

          <button
            type="button"
            className={`rcMilkBtn ${milk === "buf" ? "active" : ""}`}
            onClick={() => onChangeMilk("buf")}
          >
            <span className="rcMilkIcon">🐃</span>
            Buffalo
          </button>

          <div className="rcChartBox" title={chartTitle}>
            <span className="rcChartIcon">{milk === "cow" ? "🐄" : "🐃"}</span>
            <span className="rcChartText">{chartTitle}</span>
          </div>

          <button
            type="button"
            className="rcChangeBtn"
            onClick={() => setEditOpen(true)}
            title="Edit rate chart"
          >
            Change
          </button>
        </div>

        <div className="rcControlsMeta">
          <span className="rcMeta">
            mainuser: <b>{mainuserSafe || "-"}</b>
          </span>
          {loading ? <span className="rcMeta">Loading…</span> : null}
          {err ? <span className="rcMeta err">{err}</span> : null}
        </div>
      </div>

      <div className="rcTableWrap">
        {!loading && !err && !chartData ? (
          <div className="rcEmpty">No data</div>
        ) : isGrid ? (
          <table className="rcTable rcGrid">
            <thead>
              <tr>
                <th className="rcStickyLeft">fat_snf</th>
                {(isPoints ? pointsGrid.snfs : grid.snfs).map((snf) => (
                  <th key={snf}>{snf}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {(isPoints ? pointsGrid.fats : grid.fats).map((fat) => (
                <tr key={fat}>
                  <td className="rcStickyLeft">{fat}</td>

                  {(isPoints ? pointsGrid.snfs : grid.snfs).map((snf) => {
                    const rate = isPoints
                      ? rateRepo.getRate?.({
                          type: milk,
                          fat: Number(fat),
                          snf: Number(snf),
                          ratechartno,
                        })
                      : grid.map.get(`${fat}|${snf}`);

                    const isSel = sel?.fat === fat && sel?.snf === snf;

                    return (
                      <td
                        key={snf}
                        className={isSel ? "rcSel" : ""}
                        onClick={() => setSel({ fat, snf })}
                        style={{ cursor: "pointer" }}
                        title={`${fat} / ${snf}`}
                      >
                        {rate == null ? "" : Number(rate).toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="rcTable rcCow">
            <thead>
              <tr>
                <th>fat</th>
                <th>RATE</th>
              </tr>
            </thead>
            <tbody>
              {cowList.map((r) => (
                <tr key={r.fat}>
                  <td>{r.fat.toFixed(1)}</td>
                  <td>{Number(r.rate).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
